/**
 * @fileoverview Shared Discover envelope totals (WEB-786).
 *
 * Every chrome slot that answers "how many" — provenance strip, hero meta,
 * results bar, pick-set header — must read the SAME `meta.total` /
 * `meta.tasteMatchedTotal` pair from the shared `/events/discover`
 * envelope. Page-local `tasteMatchPct` counts and slot-specific fallbacks
 * are how four disagreeing numbers appear on one screen.
 *
 * `undefined` means the value has not resolved yet (skeleton). A number,
 * including `0`, is a measured value. `tasteMatchedTotal: null` is the
 * API-1671 "no taste signal" sentinel and must not become `0`.
 */

import type { DiscoverEventsQueryArgs, DiscoverEventsResponse } from '@loci/api';
import { isElsewhereNeighborhoodSlug } from '@loci/types';

export interface DiscoverEnvelopeTotals {
  /**
   * True once an in-range total is known — live query or SSR fallback.
   * False means every numeric slot must skeleton, never paint `0`.
   */
  readonly resolved: boolean;
  /** Corpus-wide filtered total (`meta.total`). Undefined until resolved. */
  readonly inRangeTotal: number | undefined;
  /**
   * Corpus-wide taste-matched total (`meta.tasteMatchedTotal`).
   * `undefined` — pending. `null` — resolved, no taste signal. `number` —
   * measured, including a real `0`.
   */
  readonly tasteMatchedTotal: number | null | undefined;
  /** Distinct neighborhood facet keys. Undefined until facets exist. */
  readonly neighborhoodCount: number | undefined;
  /**
   * Measured date window from `facets.scopeKey` (API-1746). Undefined until
   * the live envelope lands — consumers fall back to the URL `date` then.
   */
  readonly scopeKey: string | undefined;
}

export interface DiscoverEnvelopeTotalsFallback {
  readonly inRangeTotal?: number;
  readonly tasteMatchedTotal?: number | null;
  readonly neighborhoodCount?: number;
}

/**
 * Count distinct neighborhoods on a discover payload. Accepts the live
 * `Record<string, number>` wire shape and the legacy `{slug,count}[]`
 * fixture shape used in a few tests.
 *
 * COR-125 — the `elsewhere` catch-all ("Greater Charlotte") is not a
 * neighborhood, so it never counts toward the "N neighborhoods" the hero
 * strip prints.
 */
export function countDiscoverNeighborhoods(
  payload: DiscoverEventsResponse | undefined,
): number | undefined {
  const neighborhoods = payload?.facets?.neighborhoods;
  if (!neighborhoods) return undefined;
  const keys: readonly unknown[] = Array.isArray(neighborhoods)
    ? (neighborhoods as readonly unknown[]).map((entry) =>
        entry && typeof entry === 'object' ? (entry as { slug?: unknown }).slug : undefined,
      )
    : Object.keys(neighborhoods);
  return keys.filter((key) => !(typeof key === 'string' && isElsewhereNeighborhoodSlug(key)))
    .length;
}

/**
 * Stable, key-order-independent serialization of a discover query-args
 * object.
 *
 * Answers exactly one question: *are these still the arguments the SSR
 * fallback was measured against?* An SSR fallback describes ONE filter set.
 * Once the viewer changes a category / neighborhood / date the active args
 * differ and the fallback becomes a stale claim about a different query, so
 * it must be discarded rather than reported as resolved.
 *
 * Deliberately NOT an RTK Query cache key — `buildDiscoverQueryArgs` remains
 * the single source of truth for that (`lib/discover/queryArgs.ts`). This is
 * a local equality probe only, so it may sort keys freely: two structurally
 * identical arg objects must compare equal even when a conditional key
 * (`date`, `q`, `vibe`, …) was inserted in a different order.
 */
export function serializeDiscoverTotalsScope(args: DiscoverEventsQueryArgs): string {
  return JSON.stringify(args, (_key: string, value: unknown): unknown => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : 1)),
    );
  });
}

/**
 * Merge live envelope fields over optional SSR fallbacks. Live wins the
 * moment the shared subscription has data so every consumer settles
 * together.
 */
export function resolveDiscoverEnvelopeTotals(
  payload: DiscoverEventsResponse | undefined,
  fallback?: DiscoverEnvelopeTotalsFallback,
): DiscoverEnvelopeTotals {
  const liveTotal = payload?.meta?.total;
  const inRangeTotal = liveTotal ?? fallback?.inRangeTotal;
  const tasteMatchedTotal = payload ? payload.meta.tasteMatchedTotal : fallback?.tasteMatchedTotal;
  const neighborhoodCount = payload
    ? (countDiscoverNeighborhoods(payload) ?? fallback?.neighborhoodCount)
    : fallback?.neighborhoodCount;

  return {
    resolved: inRangeTotal !== undefined,
    inRangeTotal,
    tasteMatchedTotal,
    neighborhoodCount,
    scopeKey: payload?.facets?.scopeKey,
  };
}
